import React, {
  lazy, Suspense, useEffect, useState,
} from 'react';
import { useSelector as useReduxSelector } from 'react-redux';
import {
  Selector,
  useSelectorMachineContext,
  useSelectorMachineSend,
  SelectorContent,
  useSyncSelectionToUrl, Pricing, DeliveryOptions,
} from '@printdeal/product-selector';
import { FormattedMessage, useIntl } from 'react-intl';
import { Empty } from '@printdeal/ui-library';
import { FeatureToggle } from '@printdeal/feature-management/react';
import { isCustomerProgramReseller, useSegmentation } from '@printdeal/segmentation/react';
import { useLocation } from '@gatsbyjs/reach-router';
import queryString from 'query-string';
import {
  IProductPage,
  IPortalConfiguration,
} from '../../../../../types/product/productDetailPage';
import { RootState } from '../../../../../store';
import ProductJsonLd from '../../../detail/ProductJsonLd';
import ClientOnly from '../../../../ClientOnly';
import { CutOffTime } from '../CutOffTime';
import PriceSection from './PriceSection';
import SubmitButtons from '../SubmitButtons';
import FSCInfo from '../../../detail/advanced/summary/FSCInfo';
import ServiceSelectionSection from '../../../detail/advanced/FileServicesSection';
import { PriceCheckAlert } from '../PriceCheckAlert';

const SelectorError = lazy(() => import(/* webpackChunkName: "SelectorError" */ '../SelectorError'));
const ProductUnavailableAlert = lazy(() => import(/* webpackChunkName: "ProductUnavailableAlert" */ '../ProductUnavailableAlert'));
const TechnicalDrawingModal = lazy(() => import(/* webpackChunkName: "TechnicalDrawingModal" */ '../Modals/TechnicalDrawingModal'));

interface Props {
  selector: SelectorContent;
  data: {
    pageContent: IProductPage;
    portalConfiguration: IPortalConfiguration,
  };
  reviewPage?: boolean;
}

export const DefaultValuesSelectorSection = ({
  selector, data, reviewPage = false,
}: Props) => {
  const selectorSelection = useSelectorMachineContext((state) => state.context.selection);
  const groupSelection = useSelectorMachineContext((state) => state.context.group);
  const totalQuantity = useSelectorMachineContext((state) => state.context.totalQuantity);
  const productAttributes = useSelectorMachineContext((state) => state.context.productAttributes);
  const { portalConfiguration: { defaultShippingCountry, defaultPostalCode }, pageContent } = data;
  const servicePrices = useSelectorMachineContext((state) => state.context.servicePrices);
  const deliveryTool = useSelectorMachineContext((state) => state.context.merchantRules.deliveryTool);
  const selectedServiceInMachine = useSelectorMachineContext((state) => state.context.selectedService);
  const send = useSelectorMachineSend();
  const services = (servicePrices && pageContent.services && pageContent.services.map((service) => ({
    ...service,
    price: servicePrices[service.key],
  }))) || [];
  const shippingAddress = { CountryCode: defaultShippingCountry, PostalCode: defaultPostalCode };
  const customerGroupId = useReduxSelector<RootState, string | undefined>(
    // @ts-expect-error - The customer slice needs to be converted to typescript
    ({ customer }) => customer?.customer?.customerGroup?.id,
  );
  const intl = useIntl();
  const location = useLocation() as Location;
  const queryParams = queryString.parse(location.search);
  const [selectedServices, setSelectedServices] = useState<string[]>(
    queryParams.services ? [queryParams.services as string] : [],
  );

  const { data: segmentationData } = useSegmentation();
  const isResellerPartOfCustomerProgram = isCustomerProgramReseller(
    segmentationData?.isReseller, segmentationData?.isPartOfCustomerProgram,
  );

  useSyncSelectionToUrl();

  useEffect(() => {
    // Remove no-longer-valid selectedServices from both the selector stateMachine AND local state-management.
    if (!deliveryTool?.servicekeys) {
      // No services are valid, commence straight cleanup.
      if (selectedServiceInMachine) {
        send({ type: 'Select service', selectedService: '' });
      }
      if (selectedServices.length) {
        setSelectedServices([]);
      }
    } else {
      // Service might still be valid. Verify and cleanup.
      const validServiceKeys = deliveryTool.servicekeys?.split(',');
      if (selectedServiceInMachine && !validServiceKeys.includes(selectedServiceInMachine)) {
        send({ type: 'Select service', selectedService: '' });
      }
      setSelectedServices(selectedServices
        .filter((service) => validServiceKeys
          .find((validServiceKey) => validServiceKey.toLowerCase() === service.toLowerCase())));
    }
  }, [deliveryTool?.servicekeys, selectedServiceInMachine, selectedServices, send]);

  if (selector) {
    return (
      <>
        <ClientOnly>
          <ProductJsonLd productPage={pageContent} selector={selector} />
        </ClientOnly>

        <ClientOnly>
          <Suspense>
            <SelectorError contentfulId={pageContent.contentful_id} />
          </Suspense>
        </ClientOnly>
        <Selector
          productPageId={pageContent.contentful_id}
          selector={selector}
          shippingAddress={shippingAddress}
          customerGroupId={customerGroupId}
          isReviewPage={reviewPage}
          serviceKeys={pageContent.services?.map((service) => service.key)}
        />
        {selector.product.isPimProduct && (
          <>
            <Pricing />
            <DeliveryOptions />
          </>
        )}
        <FSCInfo productAttributes={{ validationVersion: 1, attributes: productAttributes || [] }} />
        <CutOffTime />

        {customerGroupId && isResellerPartOfCustomerProgram && (
          <div className="pd-my-4">
            <PriceCheckAlert />
          </div>
        )}

        <ClientOnly>
          <Suspense>
            <ProductUnavailableAlert
              contentfulId={pageContent.contentful_id}
              product={selector.product}
              currentSelection={selectorSelection}
              totalQuantity={totalQuantity || 0}
              groupSelection={groupSelection}
            />
          </Suspense>
        </ClientOnly>

        <ClientOnly>
          <Suspense>
            <SelectorError contentfulId={pageContent.contentful_id} />
          </Suspense>
        </ClientOnly>

        <PriceSection customerGroupId={customerGroupId} className="pd-mt-auto" />

        {services.length > 0 && (
          <FeatureToggle id="enable-filecheck-service">
            <FeatureToggle.Enabled>
              <div className="pd-mb-5">
                <ServiceSelectionSection
                  services={services}
                  selectedServices={selectedServices}
                  setSelectedServices={setSelectedServices}
                />
              </div>
            </FeatureToggle.Enabled>
          </FeatureToggle>
        )}

        <SubmitButtons
          selector={selector}
          attributes={selector.configuration.attributes}
          pageContent={pageContent}
          selectedDeliveryTool="editor"
          selectedServices={selectedServices}
        />

        <ClientOnly>
          <div className="pd-mt-5 pd-flex pd-items-center pd-justify-center">
            <Suspense>
              <TechnicalDrawingModal productPage={pageContent} variant={selector.configuration.variantSku} />
            </Suspense>
          </div>
        </ClientOnly>
      </>
    );
  }

  return (
    <div className="pd-mt-8">
      <Empty
        icon="exclamation"
        title={intl.formatMessage({
          id: 'product.configurator.outOfStock',
          defaultMessage: 'This product is currently unavailable/out of stock',
        })}
        description={<FormattedMessage id="product.configurator.outOfStockDescription" defaultMessage="Contact our customer service or request a custom quote for an alternative" />}
      />
    </div>
  );
};
